import api from "./api";
import { Category } from "../types/categories";

export async function getCategories(): Promise<Category[]> {
  const res = await api.get("/admin/categories");

  // Handle different response structures
  const responseData = res.data;

  // Check if response has a success/data wrapper structure
  if (responseData && typeof responseData === 'object' && 'success' in responseData) {
    if (responseData.success && Array.isArray(responseData.data)) {
      // Transform data to ensure proper format (id as string, etc.)
      return responseData.data.map((cat: any) => ({
        ...cat,
        id: String(cat.id || cat.category_id || cat.ID || ''),
        name: cat.name || '',
        status: cat.status || 'active',
        color_code: cat.color_code || cat.colorCode || undefined,
      }));
    }
    // If success is false, return empty array
    if (!responseData.success) {
      console.warn("Categories API returned success: false", responseData);
      return [];
    }
  }

  // If response is directly an array, return it with transformation
  if (Array.isArray(responseData)) {
    return responseData.map((cat: any) => ({
      ...cat,
      id: String(cat.id || cat.category_id || cat.ID || ''),
      name: cat.name || '',
      status: cat.status || 'active',
    }));
  }

  // If response has a data property that's an array, return it
  if (responseData?.data && Array.isArray(responseData.data)) {
    return responseData.data.map((cat: any) => ({
      ...cat,
      id: String(cat.id || cat.category_id || cat.ID || ''),
      name: cat.name || '',
      status: cat.status || 'active',
    }));
  }

  // Default: return empty array if structure is unexpected
  console.warn("Unexpected categories API response structure:", responseData);
  return [];
}

export async function createCategory(
  data: Partial<Category>
): Promise<Category> {
  const res = await api.post("/admin/categories", data);
  const responseData = res.data;

  // Handle different response structures
  if (responseData?.success && responseData?.data) {
    const cat = responseData.data;
    return {
      ...cat,
      id: String(cat.id || cat.category_id || ''),
      name: cat.name || '',
      status: cat.status || 'active',
      color_code: cat.color_code || cat.colorCode || data.color_code || undefined,
    };
  }

  // If response is directly the category object
  if (responseData && typeof responseData === 'object' && 'id' in responseData) {
    return {
      ...responseData,
      id: String(responseData.id || responseData.category_id || ''),
      status: responseData.status || 'active',
      color_code: responseData.color_code || responseData.colorCode || data.color_code || undefined,
    };
  }

  return responseData;
}

export async function getCategoryById(id: string): Promise<Category> {
  const res = await api.get(`/admin/categories/${id}`);
  const responseData = res.data;

  // Handle different response structures
  if (responseData?.success && responseData?.data) {
    const cat = responseData.data;
    return {
      ...cat,
      id: String(cat.id || cat.category_id || ''),
      name: cat.name || '',
      status: cat.status || 'active',
    };
  }

  if (responseData && typeof responseData === 'object' && 'id' in responseData) {
    return {
      ...responseData,
      id: String(responseData.id || responseData.category_id || ''),
      status: responseData.status || 'active',
    };
  }

  return responseData;
}

export async function updateCategory(
  id: string,
  data: Partial<Category>
): Promise<Category> {
  // Use POST with _method PUT for Laravel compatibility
  const res = await api.post(`/admin/categories/${id}`, {
    ...data,
    _method: 'PUT'
  });
  const responseData = res.data;

  // Handle different response structures
  if (responseData?.success && responseData?.data) {
    const cat = responseData.data;
    return {
      ...cat,
      id: String(cat.id || cat.category_id || id),
      name: cat.name || '',
      status: cat.status || 'active',
      color_code: cat.color_code || cat.colorCode || undefined,
    };
  }

  // If response is directly the category object
  if (responseData && typeof responseData === 'object' && 'id' in responseData) {
    return {
      ...responseData,
      id: String(responseData.id || responseData.category_id || id),
      status: responseData.status || 'active',
      color_code: responseData.color_code || responseData.colorCode || undefined,
    };
  }

  return responseData;
}

export async function deleteCategory(id: string): Promise<void> {
  await api.delete(`/admin/categories/${id}`);
}

export async function bulkUploadCategories(data: FormData): Promise<any> {
  const res = await api.post("/admin/categories/bulk-upload", data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function exportCategories(): Promise<Blob> {
  const res = await api.get("/admin/categories/export", {
    responseType: "blob",
  });
  return res.data;
}

export async function flushCategories(): Promise<void> {
  await api.delete("/admin/categories/flush");
}
